import {App} from '@capacitor/app';
import {Capacitor, registerPlugin, SystemBarType, SystemBars, SystemBarsStyle} from '@capacitor/core';
import type {PluginListenerHandle} from '@capacitor/core';
import {StatusBar, Style} from '@capacitor/status-bar';

export interface MobileSystemBarsBackgroundPort {
  setBackgrounds(options: {statusColor: string; navigationColor: string; webViewColor: string}): Promise<void>;
}

const appearanceSystemBars = registerPlugin<MobileSystemBarsBackgroundPort>('AppearanceSystemBars');

export async function configureMobileSystemBars(
  statusColor: string,
  navigationColor: string,
  webViewColor: string,
  statusBar: MobileStatusBarStylePort = StatusBar,
  systemBars: MobileSystemBarsStylePort = SystemBars,
  backgrounds: MobileSystemBarsBackgroundPort = appearanceSystemBars,
): Promise<void> {
  if (Capacitor.getPlatform() !== 'android') return;
  // Capacitor resets the decor background when setting either foreground style.
  // Finish both operations before applying the palette to the native inset surface.
  await Promise.all([
    configureMobileStatusBar(statusColor, statusBar, systemBars),
    systemBars.setStyle({bar: SystemBarType.NavigationBar,
      style: statusBarUsesDarkContent(navigationColor) ? SystemBarsStyle.Light : SystemBarsStyle.Dark}),
  ].map(update => update.catch(() => undefined)));
  await backgrounds.setBackgrounds({statusColor, navigationColor, webViewColor}).catch(() => undefined);
}

export interface MobileStatusBarStylePort {
  setStyle(options: {style: Style}): Promise<void>;
  setBackgroundColor(options: {color: string}): Promise<void>;
}

export interface MobileSystemBarsStylePort {
  setStyle(options: {style: SystemBarsStyle; bar: SystemBarType}): Promise<void>;
}

export interface MobileAppStatePort {
  addListener(
    eventName: 'appStateChange',
    listener: (state: {isActive: boolean}) => void,
  ): Promise<PluginListenerHandle>;
}

export function opaqueStatusBarColor(foreground: string, background: string): string {
  const alpha = foreground.length === 9 ? parseInt(foreground.slice(7), 16) / 255 : 1;
  return '#' + [1, 3, 5].map(offset => Math.round(
    parseInt(foreground.slice(offset, offset + 2), 16) * alpha
    + parseInt(background.slice(offset, offset + 2), 16) * (1 - alpha),
  ).toString(16).padStart(2, '0')).join('');
}

export function statusBarUsesDarkContent(background: string): boolean {
  const channels = [1, 3, 5].map(offset => parseInt(background.slice(offset, offset + 2), 16) / 255)
    .map(channel => channel <= 0.04045 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4));
  const luminance = channels[0] * 0.2126 + channels[1] * 0.7152 + channels[2] * 0.0722;
  return luminance > 0.179;
}

export async function configureMobileStatusBar(
  background = '#edf2f8',
  statusBar: MobileStatusBarStylePort = StatusBar,
  systemBars: MobileSystemBarsStylePort = SystemBars,
): Promise<void> {
  const darkContent = statusBarUsesDarkContent(background);
  // Older Android versions can change the icon style when setting the background.
  // Unsupported background APIs must not prevent contrast updates on newer versions.
  await statusBar.setBackgroundColor({color: background}).catch(() => undefined);
  await Promise.all([
    statusBar.setStyle({style: darkContent ? Style.Light : Style.Dark}),
    systemBars.setStyle({style: darkContent ? SystemBarsStyle.Light : SystemBarsStyle.Dark, bar: SystemBarType.StatusBar}),
  ].map(update => update.catch(() => undefined)));
}

export async function restoreMobileStatusBarOnResume(
  app: MobileAppStatePort = App,
  configureStatusBar: () => Promise<void> = configureMobileStatusBar,
): Promise<PluginListenerHandle> {
  return app.addListener('appStateChange', ({isActive}) => {
    if (isActive) {
      void configureStatusBar().catch(() => {
        // Native system-bar restoration is best-effort after returning to the app.
      });
    }
  });
}
